import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchOffices, 
  createOffice, 
  updateOffice, 
  deleteOffice,
  clearOfficeError 
} from '../../redux/slices/officeSlice';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import { Modal, Button, Form, Table, Badge, Spinner, Pagination, InputGroup } from 'react-bootstrap';
import Select from 'react-select';

const OfficeTreeNode = ({ node, level = 0, onEdit, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  return (
    <div className="office-tree-node" style={{ marginLeft: level > 0 ? '30px' : '0' }}>
      <div className="d-flex align-items-center py-2 border-bottom" style={{ gap: '10px' }}>
        {node.children.length > 0 ? (
          <button 
            className="btn btn-sm btn-outline-secondary p-0 d-flex align-items-center justify-content-center" 
            style={{ width: '24px', height: '24px' }}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? '−' : '+'}
          </button>
        ) : (
          <div style={{ width: '24px' }}></div>
        )}
        <div className="flex-grow-1">
          <span className="fw-medium">{node.name}</span>
          <Badge bg="info" className="ms-2">{node.type.replace('_', ' ').toUpperCase()}</Badge>
          {node.head_user && (
            <small className="text-muted ms-3">
              Director: {node.head_user.name}
            </small>
          )}
        </div>
        <div>
          <Button variant="light" size="sm" className="me-1" onClick={() => onEdit(node)}>
            <i className="uil-edit text-primary"></i> Edit
          </Button>
          <Button variant="light" size="sm" onClick={() => onDelete(node.id)}>
            <i className="uil-trash-alt text-danger"></i> Delete
          </Button>
        </div>
      </div>
      {isExpanded && node.children.length > 0 && (
        <div className="office-tree-children border-start ms-2 border-2 border-light">
          {node.children.map(child => (
            <OfficeTreeNode 
              key={child.id} 
              node={child} 
              level={level + 1} 
              onEdit={onEdit} 
              onDelete={onDelete} 
            />
          ))}
        </div>
      )}
    </div>
  );
};

const OfficesPage = () => {
  const dispatch = useDispatch();
  const { offices, status, error } = useSelector((state) => state.offices);
  
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentOffice, setCurrentOffice] = useState(null);
  
  // Search & Pagination State
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // View Mode
  const [viewMode, setViewMode] = useState("list"); // 'list' or 'tree'
  
  const [formData, setFormData] = useState({
    name: "",
    type: "branch_office",
    parent_id: "",
    head_user_id: "",
    portal_slug: "",
  });

  const [showStaffModal, setShowStaffModal] = useState(false);
  const [staffOffice, setStaffOffice] = useState(null);
  const [officeStaff, setOfficeStaff] = useState([]);
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);

  // Load companies & users on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [compRes, userRes] = await Promise.all([
          api.get("/api/companies"),
          api.get("/api/users?per_page=all")
        ]);
        setCompanies(compRes.data);
        if (compRes.data.length > 0) {
          setSelectedCompanyId(compRes.data[0].id);
        }
        setUsers(userRes.data);
      } catch (err) {
        toast.error("Failed to load initial data");
      }
    };
    loadInitialData();
  }, []);

  // Load offices when company changes
  useEffect(() => {
    if (selectedCompanyId) {
      dispatch(fetchOffices(selectedCompanyId));
    }
  }, [selectedCompanyId, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearOfficeError());
    }
  }, [error, dispatch]);

  // Reset pagination on search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleCompanyChange = (e) => {
    setSelectedCompanyId(e.target.value);
  };

  const openAddModal = () => {
    setIsEditing(false);
    setCurrentOffice(null);
    setFormData({
      name: "",
      type: "branch_office",
      parent_id: "",
      head_user_id: "",
      portal_slug: "",
    });
    setShowModal(true);
  };

  const openEditModal = (office) => {
    setIsEditing(true);
    setCurrentOffice(office);
    setFormData({
      name: office.name,
      type: office.type,
      parent_id: office.parent_id || "",
      head_user_id: office.head_user_id || "",
      portal_slug: office.portal_slug || "",
    });
    setShowModal(true);
  };

  const openStaffModal = async (office) => {
    setStaffOffice(office);
    setSelectedStaffIds([]);
    setShowStaffModal(true);
    setStaffLoading(true);
    try {
      const response = await api.get(`/api/admin/offices/${office.id}/staff`);
      setOfficeStaff(response.data.staff || []);
    } catch (err) {
      toast.error("Failed to load office staff");
      setOfficeStaff([]);
    } finally {
      setStaffLoading(false);
    }
  };

  const copyPortalLink = (slug) => {
    const url = `${window.location.origin}/office-portal/${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success("Portal link copied to clipboard");
    }).catch(() => {
      toast.info(url);
    });
  };

  const handleAssignStaff = async () => {
    if (!staffOffice || selectedStaffIds.length === 0) {
      toast.error("Select at least one user to assign");
      return;
    }

    try {
      await api.post(`/api/admin/offices/${staffOffice.id}/staff`, {
        user_ids: selectedStaffIds,
      });
      toast.success("Staff assigned successfully");
      setSelectedStaffIds([]);
      const response = await api.get(`/api/admin/offices/${staffOffice.id}/staff`);
      setOfficeStaff(response.data.staff || []);
      if (selectedCompanyId) {
        dispatch(fetchOffices(selectedCompanyId));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to assign staff");
    }
  };

  const handleRemoveStaff = async (userId) => {
    if (!staffOffice) return;

    try {
      await api.delete(`/api/admin/offices/${staffOffice.id}/staff/${userId}`);
      toast.success("Staff removed from office");
      setOfficeStaff((prev) => prev.filter((member) => member.id !== userId));
      if (selectedCompanyId) {
        dispatch(fetchOffices(selectedCompanyId));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to remove staff");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this office?")) {
      try {
        await dispatch(deleteOffice(id)).unwrap();
        toast.success("Office deleted successfully!");
      } catch (err) {
        toast.error(err || "Failed to delete office");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error("Name is required");
      return;
    }

    const payload = {
      company_id: selectedCompanyId,
      name: formData.name,
      type: formData.type,
      parent_id: formData.parent_id ? parseInt(formData.parent_id) : null,
      head_user_id: formData.head_user_id ? parseInt(formData.head_user_id) : null,
    };

    if (formData.type === "director_office" && formData.portal_slug) {
      payload.portal_slug = formData.portal_slug.trim();
    }

    try {
      if (isEditing) {
        await dispatch(updateOffice({ id: currentOffice.id, data: payload })).unwrap();
        toast.success("Office updated successfully!");
      } else {
        await dispatch(createOffice(payload)).unwrap();
        toast.success("Office created successfully!");
      }
      setShowModal(false);
    } catch (err) {
      toast.error(err || "Failed to save office");
    }
  };

  const formatType = (type) => {
    if (!type) return '';
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Filter & Pagination Logic
  const filteredOffices = (Array.isArray(offices) ? offices : []).filter(office => 
    (office.name || "").toLowerCase().includes(searchQuery.toLowerCase()) || 
    (office.type || "").toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOffices = filteredOffices.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredOffices.length / itemsPerPage);

  // Tree Logic
  const buildOfficeTree = () => {
    const map = {};
    const roots = [];
    
    // First pass: create nodes
    (Array.isArray(offices) ? offices : []).forEach(office => {
      map[office.id] = { ...office, children: [] };
    });
    
    // Second pass: build relationships
    (Array.isArray(offices) ? offices : []).forEach(office => {
      if (office.parent_id && map[office.parent_id]) {
        map[office.parent_id].children.push(map[office.id]);
      } else {
        roots.push(map[office.id]);
      }
    });
    
    return roots;
  };

  const treeRoots = buildOfficeTree();

  // React Select Options
  const userList = Array.isArray(users) ? users : (users?.data || []);
  const userOptions = userList.map(u => ({ value: u.id, label: `${u.name} (${u.email})` }));
  const officeOptions = (Array.isArray(offices) ? offices : []).filter(o => o.id !== currentOffice?.id).map(o => ({ value: o.id, label: `${o.name} (${formatType(o.type)})` }));
  const unassignedUserOptions = userList
    .filter((user) => !officeStaff.some((member) => member.id === user.id))
    .map((user) => ({ value: user.id, label: `${user.name} (${user.email})` }));

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h4>Office Management</h4>
        <div className="d-flex gap-3 align-items-center flex-wrap">
          <div className="btn-group">
            <button 
              className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setViewMode('list')}
            >
              <i className="uil-list-ul"></i> List
            </button>
            <button 
              className={`btn btn-sm ${viewMode === 'tree' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setViewMode('tree')}
            >
              <i className="uil-sitemap"></i> Tree
            </button>
          </div>
          <select 
            className="form-select" 
            style={{ width: 'auto' }}
            value={selectedCompanyId} 
            onChange={handleCompanyChange}
          >
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <Button variant="primary" onClick={openAddModal} disabled={!selectedCompanyId}>
            <i className="uil-plus me-1"></i> Add Office
          </Button>
        </div>
      </div>

      <div className="card shadow-sm">
        {viewMode === 'list' && (
          <div className="card-header bg-white py-3">
            <div className="row">
              <div className="col-md-4">
                <InputGroup>
                  <InputGroup.Text><i className="uil-search"></i></InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search offices..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </InputGroup>
              </div>
            </div>
          </div>
        )}
        
        <div className="card-body p-0">
          {status === 'loading' ? (
            <div className="p-5 text-center"><Spinner animation="border" /></div>
          ) : viewMode === 'list' ? (
            <>
              <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Parent Office</th>
                    <th>Head Director</th>
                    <th>Portal</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentOffices.length === 0 ? (
                    <tr><td colSpan="6" className="text-center py-4">No offices found.</td></tr>
                  ) : (
                    currentOffices.map(office => (
                      <tr key={office.id}>
                        <td className="fw-medium">{office.name}</td>
                        <td><Badge bg="info">{formatType(office.type)}</Badge></td>
                        <td>{office.parent?.name || '-'}</td>
                        <td>
                          {office.head_user ? (
                            <span>{office.head_user.name} <br/><small className="text-muted">{office.head_user.email}</small></span>
                          ) : (
                            <span className="text-muted fst-italic">Not Assigned</span>
                          )}
                        </td>
                        <td>
                          {office.type === "director_office" && office.portal_slug ? (
                            <div className="d-flex flex-column gap-1">
                              <code>/office-portal/{office.portal_slug}</code>
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-start"
                                onClick={() => copyPortalLink(office.portal_slug)}
                              >
                                Copy link
                              </Button>
                            </div>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-end">
                          {office.type === "director_office" && (
                            <Button variant="light" size="sm" className="me-2" onClick={() => openStaffModal(office)}>
                              <i className="uil-users-alt text-success"></i>
                            </Button>
                          )}
                          <Button variant="light" size="sm" className="me-2" onClick={() => openEditModal(office)}>
                            <i className="uil-edit text-primary"></i>
                          </Button>
                          <Button variant="light" size="sm" onClick={() => handleDelete(office.id)}>
                            <i className="uil-trash-alt text-danger"></i>
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
              {totalPages > 1 && (
                <div className="d-flex justify-content-end p-3 border-top">
                  <Pagination className="mb-0">
                    <Pagination.Prev 
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                      disabled={currentPage === 1} 
                    />
                    {[...Array(totalPages)].map((_, i) => (
                      <Pagination.Item 
                        key={i + 1} 
                        active={i + 1 === currentPage}
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next 
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                      disabled={currentPage === totalPages} 
                    />
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <div className="p-4">
              {treeRoots.length === 0 ? (
                <div className="text-center py-5 text-muted">No offices to display in tree.</div>
              ) : (
                <div className="office-tree-wrapper">
                  {treeRoots.map(root => (
                    <OfficeTreeNode 
                      key={root.id} 
                      node={root} 
                      onEdit={openEditModal} 
                      onDelete={handleDelete} 
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Edit Office' : 'Create Office'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form id="officeForm" onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Office Name *</Form.Label>
                  <Form.Control 
                    type="text" 
                    required 
                    value={formData.name} 
                    onChange={(e) => setFormData({...formData, name: e.target.value})} 
                    placeholder="e.g., Dhaka North Branch"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Office Type</Form.Label>
                  <Form.Select 
                    value={formData.type} 
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                  >
                    <option value="head_office">Head Office</option>
                    <option value="regional_office">Regional Office</option>
                    <option value="branch_office">Branch Office</option>
                    <option value="ed_office">ED Office</option>
                    <option value="director_office">Director Office</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            {formData.type === "director_office" && (
              <Form.Group className="mb-3">
                <Form.Label>Portal Slug</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.portal_slug}
                  onChange={(e) => setFormData({ ...formData, portal_slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })}
                  placeholder="e.g., iconic-furniture"
                />
                <Form.Text className="text-muted">
                  Unique URL segment for this director office portal. Leave blank to auto-generate.
                </Form.Text>
              </Form.Group>
            )}

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Parent Office</Form.Label>
                  <Select
                    options={officeOptions}
                    isClearable
                    placeholder="Select parent office..."
                    value={officeOptions.find(opt => opt.value === formData.parent_id) || null}
                    onChange={(selected) => setFormData({...formData, parent_id: selected ? selected.value : ""})}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Head Director</Form.Label>
                  <Select
                    options={userOptions}
                    isClearable
                    placeholder="Search for a director..."
                    value={userOptions.find(opt => opt.value === formData.head_user_id) || null}
                    onChange={(selected) => setFormData({...formData, head_user_id: selected ? selected.value : ""})}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  />
                  <Form.Text className="text-muted">You can assign a director to this office.</Form.Text>
                </Form.Group>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" type="submit" form="officeForm">Save Office</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showStaffModal} onHide={() => setShowStaffModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Manage Staff — {staffOffice?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {staffLoading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : (
            <>
              <div className="mb-4">
                <Form.Label>Assign staff to this office</Form.Label>
                <Select
                  isMulti
                  options={unassignedUserOptions}
                  placeholder="Select users..."
                  value={unassignedUserOptions.filter((opt) => selectedStaffIds.includes(opt.value))}
                  onChange={(selected) => setSelectedStaffIds((selected || []).map((opt) => opt.value))}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
                <div className="mt-2">
                  <Button variant="primary" size="sm" onClick={handleAssignStaff}>
                    Assign Selected
                  </Button>
                </div>
              </div>

              <h6 className="mb-3">Current staff</h6>
              {officeStaff.length === 0 ? (
                <p className="text-muted mb-0">No staff assigned yet.</p>
              ) : (
                <Table responsive size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {officeStaff.map((member) => (
                      <tr key={member.id}>
                        <td>{member.name}</td>
                        <td>{member.email}</td>
                        <td className="text-end">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveStaff(member.id)}
                          >
                            Remove
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStaffModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OfficesPage;
